import React, { useState, useMemo } from 'react';
import { inject, observer } from 'mobx-react';
import compose from 'helpers/compose';
import { useTranslation } from 'react-i18next';

import GetAppIcon from '@material-ui/icons/GetApp';
import Visibility from '@material-ui/icons/Visibility';
import CheckIcon from '@material-ui/icons/Check';
import { CircularProgress } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import MobileFriendlyIcon from '@material-ui/icons/MobileFriendly';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import FindInPageOutlinedIcon from '@material-ui/icons/FindInPageOutlined';
import SystemUpdateOutlinedIcon from '@material-ui/icons/SystemUpdateOutlined';

import { isoDateTimeToLabel } from 'helpers/helpers';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';

import { InvestmentPlan } from 'apps/Pdf/templates/InvestmentPlan/InvestmentPlan';
import { PriceList } from 'apps/Pdf/templates/PriceList/PriceList';
import { MainContract } from 'apps/Pdf/templates/MainContract/MainContract';
import { InvestmentQuestionnaire } from 'apps/Pdf/templates/InvestmentQuestionnaire/InvestmentQuestionnaire';
import { FinancialQuestionnaire } from 'apps/Pdf/templates/FinancialQuestionnaire/FinancialQuestionnaire';
import { MeetingRecord } from 'apps/Pdf/templates/MeetingRecord/MeetingRecord';
import { MoneyWithdrawal } from 'apps/Pdf/templates/MoneyWithdrawal/MoneyWithdrawal';
import { IncomeOwnership } from 'apps/Pdf/templates/IncomeOwnership/IncomeOwnership';
import { NewPersonalData } from 'apps/Pdf/templates/NewPersonalData/NewPersonalData';
import { LTIP } from 'apps/Pdf/templates/LTIP/LTIP';
import { LTIPTermination } from 'apps/Pdf/templates/LTIPTermination/LTIPTermination';
import { MoneyTransfer } from 'apps/Pdf/templates/MoneyTransfer/MoneyTransfer';

import { RootStore } from 'stores/rootStore';
import { SignatureStore } from 'stores/account/signatureStore';
import { AccountStatus } from 'enums/index';
import { SignatureStatus } from 'enums';
import PdfPreview from 'components/PdfPreview/PdfPreview';
import { InvalidState } from './Signature';

import useStyles from 'pages/Accounts/Detail/Signature/styles/Documents';

interface Props {
  rootStore: RootStore;
  store: SignatureStore;
  signOnly?: boolean;
}

const showPDF = (
  rootStore: RootStore,
  pdfType?: string,
  pdfId?: string,
  change?: boolean,
  afterLoad?: () => void,
  onError?: () => void,
) => {
  switch (pdfType) {
    case 'PRICE_LIST':
      return (
        <PriceList
          accountId={pdfId}
          change={change}
          rootStore={rootStore}
          afterLoad={afterLoad}
          onError={onError}
          preview
        />
      );
    case 'MAIN_CONTRACT':
      return (
        <MainContract
          accountId={pdfId}
          change={change}
          rootStore={rootStore}
          afterLoad={afterLoad}
          onError={onError}
          preview
        />
      );
    case 'INVESTMENT_QUESTIONNAIRE':
      return (
        <InvestmentQuestionnaire
          accountId={pdfId}
          change={change}
          rootStore={rootStore}
          afterLoad={afterLoad}
          onError={onError}
          preview
        />
      );
    case 'FINANCIAL_QUESTIONNAIRE':
      return (
        <FinancialQuestionnaire
          accountId={pdfId}
          change={change}
          rootStore={rootStore}
          afterLoad={afterLoad}
          onError={onError}
          preview
        />
      );
    case 'MEETING_RECORD':
      return <MeetingRecord accountId={pdfId} rootStore={rootStore} afterLoad={afterLoad} onError={onError} preview />;
    case 'INVESTMENT_PLAN':
      return (
        <InvestmentPlan
          accountId={pdfId}
          change={change}
          rootStore={rootStore}
          afterLoad={afterLoad}
          onError={onError}
          preview
        />
      );
    case 'MONEY_WITHDRAWAL':
      return (
        <MoneyWithdrawal
          accountId={pdfId}
          change={change}
          rootStore={rootStore}
          afterLoad={afterLoad}
          onError={onError}
          preview
        />
      );
    case 'INCOME_OWNERSHIP':
      return (
        <IncomeOwnership accountId={pdfId} rootStore={rootStore} afterLoad={afterLoad} onError={onError} preview />
      );
    case 'PERSONAL_DATA':
      return (
        <NewPersonalData
          accountId={pdfId}
          change={change}
          rootStore={rootStore}
          afterLoad={afterLoad}
          onError={onError}
          preview
        />
      );
    case 'LTIP_AGREEMENT':
      return <LTIP accountId={pdfId} rootStore={rootStore} afterLoad={afterLoad} onError={onError} preview />;
    case 'LTIP_TERMINATION':
      return (
        <LTIPTermination accountId={pdfId} rootStore={rootStore} afterLoad={afterLoad} onError={onError} preview />
      );
    case 'MONEY_TRANSFER':
      return <MoneyTransfer accountId={pdfId} rootStore={rootStore} afterLoad={afterLoad} onError={onError} preview />;
    default:
      return null;
  }
};

const Documents: React.FC<Props> = ({ rootStore, store, signOnly }) => {
  const { t } = useTranslation();

  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfType, setPdfType] = useState<string | undefined>(undefined);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmType, setConfirmType] = useState('');
  const [confirmDisabled, setConfirmDisabled] = useState(false);

  const classes = useStyles();

  const mobile = useMediaQuery('(max-width:640px)', { noSsr: true });

  const pdfPreview = useMemo(
    () => (
      <PdfPreview
        open={pdfOpen}
        handleClose={() => {
          setPdfOpen(false);
          setPdfType(undefined);
        }}
        functionDisabled={confirmDisabled}
        miscFunction={() => {
          pdfType && setConfirmType(pdfType);
          setConfirmDialogOpen(true);
        }}
        miscText="general_confirm"
        miscIcon={<CheckIcon />}
        miscMobileIcon={<MobileFriendlyIcon />}
        renderTemplate={
          pdfType
            ? (afterLoad: undefined) =>
                showPDF(rootStore, pdfType, store.accountId, store.change, afterLoad, () =>
                  rootStore.stores.uiStore.stopLoading('pdfPreview'),
                )
            : () => null
        }
      />
    ),
    [pdfType, confirmDisabled, pdfOpen, rootStore, store.accountId, store.change],
  );

  if (rootStore.stores.userStore.activeAccount?.process === SignatureStatus.SIGNED) {
    return signOnly
      ? InvalidState(t('documents_sign_noDocs'), true)
      : InvalidState(
          <span
            dangerouslySetInnerHTML={{
              __html: t('documents_sign_processingSignature', {
                interpolation: { escapeValue: false },
              }),
            }}
          />,
          true,
        );
  }
  if (rootStore.stores.userStore.activeAccount?.process === SignatureStatus.EXPIRED) {
    return InvalidState(t('documents_sign_signatureExpired'), true);
  }

  return (
    <Container maxWidth="md">
      {store.documents && store.documents.length > 0 && (
        <>
          <Typography variant="h1" className={classes.title}>
            {t('documents_title')}
          </Typography>
          <Typography align="justify">
            {t('documents_info')}
            {store.advisor?.name}, {store.advisor?.phone},{' '}
            <a href={`mailto:${store.advisor?.email}`}>{store.advisor?.email}</a>
          </Typography>

          <Grid container className={classes.docs} spacing={0}>
            {store.documents.map((doc) => (
              <Grid container item xs={12} alignItems="center" className={classes.docLine} key={doc.type} spacing={0}>
                <Grid item container xs={7} sm={9}>
                  <Grid item xs={12} sm={6}>
                    {t(`pdf_document_${doc.type}${store.status === AccountStatus.AWAITING_APPROVAL ? '_03' : ''}`)}
                  </Grid>
                  <Grid container item xs={12} sm={6} className={classes.actions}>
                    <Button
                      variant={mobile ? 'text' : 'outlined'}
                      color={mobile ? 'primary' : undefined}
                      //startIcon={<Visibility />}
                      startIcon={mobile ? <FindInPageOutlinedIcon fontSize="large" /> : <Visibility />}
                      size={mobile ? 'large' : 'medium'}
                      onClick={() => {
                        rootStore.stores.uiStore.startLoading('pdfPreview');
                        setConfirmDisabled(!!(doc.confirmed && doc.confirmedOn));
                        setPdfType(doc.type);
                        setPdfOpen(true);
                      }}
                      className={classes.viewButton}
                    >
                      {!mobile && t('general_preview')}
                    </Button>
                    <Button
                      variant={mobile ? 'text' : 'outlined'}
                      color={mobile ? 'primary' : undefined}
                      size={mobile ? 'large' : 'medium'}
                      startIcon={
                        doc.loading ? (
                          <CircularProgress size={mobile ? 28 : 20} />
                        ) : mobile ? (
                          <SystemUpdateOutlinedIcon fontSize="large" />
                        ) : (
                          <GetAppIcon />
                        )
                      }
                      onClick={() => store.downloadDocument(doc.type)}
                      className={classes.viewButton}
                    >
                      {!mobile && t('general_pdf')}
                    </Button>
                  </Grid>
                </Grid>
                <Grid
                  item
                  xs={5}
                  sm={3}
                  className={doc.confirmed && doc.confirmedOn ? classes.confirmedWrapper : undefined}
                >
                  {doc.confirmed && doc.confirmedOn ? (
                    <div className={classes.confirmed}>
                      <strong>
                        <CheckIcon />
                        {t('document_confirmed_on')}
                      </strong>
                      <span>{isoDateTimeToLabel(doc.confirmedOn)}</span>
                    </div>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setConfirmType(doc.type);
                        setConfirmDialogOpen(true);
                      }}
                      className={classes.confirmButton}
                    >
                      {t('general_confirm')}
                    </Button>
                  )}
                </Grid>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {pdfPreview}

      <ConfirmDialog
        isOpen={confirmDialogOpen}
        title="general_confirm"
        close={() => setConfirmDialogOpen(false)}
        confirm={async () => {
          await store.confirmDocument(confirmType);
          setConfirmDialogOpen(false);
          setPdfOpen(false);
          setPdfType(undefined);
        }}
        btnConfirmText="general_confirm"
      >
        <Typography variant="h3">{t(`pdf_document_${confirmType}`)}</Typography>
        <Typography>{t('document_sign_info')}</Typography>
      </ConfirmDialog>
    </Container>
  );
};

export default compose(inject('rootStore'), observer)(Documents);
